/**
 * Het Koffiepaleis - Ecommerce Tracking Events
 * GA4 Enhanced Ecommerce via GTM dataLayer
 * 
 * Events:
 * - view_item_list
 * - select_item
 * - view_item
 * - add_to_cart
 * - remove_from_cart
 * - view_cart
 * - begin_checkout
 * - add_shipping_info
 * - add_payment_info
 * - purchase
 */

(function() {
  'use strict';

  // Testmodus (bijv. Playwright): geen dataLayer-events pushen of listeners registreren.
  // Zie snippets/tracking-guard.liquid en docs/ideation/037_playwright_tests_zonder_gtm_ga_tracking.md
  if (window.KP_TRACKING_DISABLED) {
    return;
  }

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  /**
   * Utility: Format price for dataLayer
   * Converteert Shopify cents naar decimaal
   */
  function formatPrice(priceInCents) {
    var cents = parseInt(priceInCents, 10);
    if (isNaN(cents)) return '0.00';
    return (cents / 100).toFixed(2);
  }

  /**
   * Utility: Get UTM parameters from session storage
   */
  function getUtmParams() {
    var utmData = sessionStorage.getItem('utmData');
    if (utmData) {
      try {
        return JSON.parse(utmData);
      } catch (e) {
        return {};
      }
    }
    return {};
  }

  /**
   * Track: select_item
   * Wanneer een gebruiker op een product klikt in een lijst
   */
  function trackSelectItem(productData, listName, listId, index) {
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push({
      'event': 'select_item',
      'ecommerce': {
        'item_list_id': listId || '',
        'item_list_name': listName || '',
        'items': [{
          'item_id': productData.id,
          'item_name': productData.title,
          'item_brand': productData.vendor || 'Het Koffiepaleis',
          'item_category': productData.type || '',
          'item_variant': productData.variant || '',
          'price': parseFloat(formatPrice(productData.price)),
          'index': index || 0
        }]
      }
    });
  }

  /**
   * Track: add_to_cart
   * Wanneer een product aan de winkelwagen wordt toegevoegd
   */
  function trackAddToCart(productData, quantity) {
    quantity = quantity || 1;
    
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'add_to_cart',
      'ecommerce': {
        'currency': window.Shopify?.currency?.active || 'EUR',
        'value': parseFloat(formatPrice(productData.price * quantity)),
        'items': [{
          'item_id': productData.id,
          'item_name': productData.title,
          'item_brand': productData.vendor || 'Het Koffiepaleis',
          'item_category': productData.type || '',
          'item_variant': productData.variant || '',
          'price': parseFloat(formatPrice(productData.price)),
          'quantity': quantity
        }]
      }
    });
  }

  /**
   * Track: remove_from_cart
   * Wanneer een product uit de winkelwagen wordt verwijderd
   */
  function trackRemoveFromCart(productData, quantity) {
    quantity = quantity || 1;
    
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'remove_from_cart',
      'ecommerce': {
        'currency': window.Shopify?.currency?.active || 'EUR',
        'value': parseFloat(formatPrice(productData.price * quantity)),
        'items': [{
          'item_id': productData.id,
          'item_name': productData.title,
          'item_brand': productData.vendor || 'Het Koffiepaleis',
          'item_category': productData.type || '',
          'item_variant': productData.variant || '',
          'price': parseFloat(formatPrice(productData.price)),
          'quantity': quantity
        }]
      }
    });
  }

  /**
   * Track: view_cart
   * Wanneer de cart pagina of drawer wordt geopend
   */
  function trackViewCart(cartData) {
    if (!cartData || !cartData.items) return;
    
    var items = cartData.items.map(function(item, index) {
      return {
        'item_id': item.product_id,
        'item_name': item.product_title,
        'item_brand': item.vendor || 'Het Koffiepaleis',
        'item_category': item.product_type || '',
        'item_variant': item.variant_title || '',
        'price': parseFloat(formatPrice(item.final_price)),
        'quantity': item.quantity,
        'index': index
      };
    });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'view_cart',
      'ecommerce': {
        'currency': cartData.currency || 'EUR',
        'value': parseFloat(formatPrice(cartData.total_price)),
        'items': items
      }
    });
  }

  /**
   * Track: begin_checkout
   * Wanneer de checkout wordt gestart
   */
  function trackBeginCheckout(cartData) {
    if (!cartData || !cartData.items) return;
    
    var items = cartData.items.map(function(item, index) {
      return {
        'item_id': item.product_id,
        'item_name': item.product_title,
        'item_brand': item.vendor || 'Het Koffiepaleis',
        'item_category': item.product_type || '',
        'item_variant': item.variant_title || '',
        'price': parseFloat(formatPrice(item.final_price)),
        'quantity': item.quantity,
        'index': index
      };
    });

    var utmParams = getUtmParams();

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'begin_checkout',
      'ecommerce': {
        'currency': cartData.currency || 'EUR',
        'value': parseFloat(formatPrice(cartData.total_price)),
        'items': items
      },
      'traffic_source': utmParams.utm_source || '',
      'traffic_medium': utmParams.utm_medium || '',
      'traffic_campaign': utmParams.utm_campaign || ''
    });
  }

  /**
   * Track: add_shipping_info
   * Wanneer verzendmethode wordt geselecteerd (checkout)
   */
  function trackAddShippingInfo(cartData, shippingTier) {
    if (!cartData || !cartData.items) return;
    
    var items = cartData.items.map(function(item, index) {
      return {
        'item_id': item.product_id,
        'item_name': item.product_title,
        'item_variant': item.variant_title || '',
        'price': parseFloat(formatPrice(item.final_price)),
        'quantity': item.quantity,
        'index': index
      };
    });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'add_shipping_info',
      'ecommerce': {
        'currency': cartData.currency || 'EUR',
        'value': parseFloat(formatPrice(cartData.total_price)),
        'shipping_tier': shippingTier || 'standard',
        'items': items
      }
    });
  }

  /**
   * Track: add_payment_info
   * Wanneer betaalmethode wordt geselecteerd (checkout)
   */
  function trackAddPaymentInfo(cartData, paymentType) {
    if (!cartData || !cartData.items) return;
    
    var items = cartData.items.map(function(item, index) {
      return {
        'item_id': item.product_id,
        'item_name': item.product_title,
        'item_variant': item.variant_title || '',
        'price': parseFloat(formatPrice(item.final_price)),
        'quantity': item.quantity,
        'index': index
      };
    });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'add_payment_info',
      'ecommerce': {
        'currency': cartData.currency || 'EUR',
        'value': parseFloat(formatPrice(cartData.total_price)),
        'payment_type': paymentType || 'unknown',
        'items': items
      }
    });
  }

  function getHelpEntryContext() {
    var body = document.body;
    var collectionGrid = document.querySelector('[data-collection-grid]');

    return {
      pageType: body?.dataset?.templateName || '',
      pageTemplate: body?.dataset?.templateSuffix || '',
      collectionHandle: collectionGrid?.dataset?.collectionHandle || '',
      collectionTitle: collectionGrid?.dataset?.collectionTitle || ''
    };
  }

  function trackHelpEntryClick(entryData) {
    window.dataLayer.push({
      'event': 'help_entry_click',
      'help_entry_type': entryData.type || '',
      'help_entry_location': entryData.location || '',
      'help_entry_label': entryData.label || '',
      'help_entry_url': entryData.url || '',
      'page_type': entryData.pageType || '',
      'page_template': entryData.pageTemplate || '',
      'collection_handle': entryData.collectionHandle || '',
      'collection_title': entryData.collectionTitle || ''
    });
  }

  /**
   * Track: select_content (generieke CTA / engagement)
   * GA4-recommended event voor knop-engagement op storefront-CTA's.
   * Zie ADR-003 en docs/TRACKING-MEETPLAN.md sectie "Generieke knoppen".
   *
   * Conventie:
   *   <a|button data-track-click
   *             data-track-category="hero"
   *             data-track-action="cta_primary"
   *             data-track-label="shop_koffiebonen"      (snake_case, EN, technische id)
   *             data-track-location="homepage_hero"      (optioneel)
   *             data-track-section-id="{{ section.id }}"
   *             data-track-block-id="{{ block.id }}">
   */
  function trackSelectContent(data) {
    var category = data.category || '';
    var action = data.action || '';
    var label = data.label || '';
    var body = document.body;

    window.dataLayer.push({
      'event': 'select_content',
      'content_type': category,
      'content_id': action && label ? (action + '/' + label) : (action || label || ''),
      'click_category': category,
      'click_action': action,
      'click_label': label,
      'click_location': data.location || '',
      'click_url': data.url || '',
      'section_id': data.sectionId || '',
      'block_id': data.blockId || '',
      'page_type': body && body.dataset ? body.dataset.templateName || '' : '',
      'page_template': body && body.dataset ? body.dataset.templateSuffix || '' : ''
    });
  }

  /**
   * Selectors van elementen die hun eigen specifieke event krijgen en
   * dus NOOIT een select_content-event mogen triggeren (geen dubbeltelling).
   * Zie ADR-003.
   */
  var TRACK_CLICK_EXCLUDE_SELECTORS = [
    '[data-add-to-cart]',
    '[data-checkout-button]',
    '[name="checkout"]',
    '[data-product-card]',
    '[data-help-entry-point]'
  ].join(',');

  /**
   * Expose tracking functions globally
   */
  window.KoffieTracking = {
    selectItem: trackSelectItem,
    addToCart: trackAddToCart,
    removeFromCart: trackRemoveFromCart,
    viewCart: trackViewCart,
    beginCheckout: trackBeginCheckout,
    addShippingInfo: trackAddShippingInfo,
    addPaymentInfo: trackAddPaymentInfo,
    helpEntryClick: trackHelpEntryClick,
    selectContent: trackSelectContent,
    formatPrice: formatPrice
  };

  /**
   * Auto-bind click events voor product cards
   */
  document.addEventListener('DOMContentLoaded', function() {
    // Product card clicks
    document.querySelectorAll('[data-product-card]').forEach(function(card, index) {
      card.addEventListener('click', function(e) {
        // Don't track if clicking add to cart button
        if (e.target.closest('[data-add-to-cart]')) return;
        
        var productData = {
          id: card.dataset.productId,
          title: card.dataset.productTitle,
          price: card.dataset.productPrice,
          vendor: card.dataset.productVendor,
          type: card.dataset.productType,
          variant: card.dataset.productVariant
        };
        
        var listName = card.closest('[data-collection-title]')?.dataset.collectionTitle || '';
        var listId = card.closest('[data-collection-handle]')?.dataset.collectionHandle || '';
        
        trackSelectItem(productData, listName, listId, index);
      });
    });

    // Add to cart form submissions
    document.querySelectorAll('form[action="/cart/add"]').forEach(function(form) {
      form.addEventListener('submit', function() {
        var productData = {
          id: form.querySelector('[name="id"]')?.value,
          title: form.dataset.productTitle || '',
          price: form.dataset.productPrice || 0,
          vendor: form.dataset.productVendor || '',
          type: form.dataset.productType || '',
          variant: form.querySelector('[name="id"]:checked')?.dataset.variantTitle || ''
        };
        
        var quantity = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
        trackAddToCart(productData, quantity);
      });
    });

    // Checkout button clicks
    document.querySelectorAll('[data-checkout-button], [name="checkout"]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        fetch('/cart.js')
          .then(function(response) { return response.json(); })
          .then(function(cart) {
            trackBeginCheckout(cart);
          });
      });
    });

    document.addEventListener('click', function(event) {
      var helpEntry = event.target.closest('[data-help-entry-point]');
      if (!helpEntry) return;

      var helpContext = getHelpEntryContext();
      trackHelpEntryClick({
        type: helpEntry.dataset.helpEntryType,
        location: helpEntry.dataset.helpEntryLocation,
        label: helpEntry.dataset.helpEntryLabel || helpEntry.textContent.trim(),
        url: helpEntry.href || helpEntry.getAttribute('href') || '',
        pageType: helpContext.pageType,
        pageTemplate: helpContext.pageTemplate,
        collectionHandle: helpContext.collectionHandle,
        collectionTitle: helpContext.collectionTitle
      });
    });

    // Generieke select_content-tracking voor [data-track-click] op <a> en <button>.
    // Zie ADR-003 voor de conventie en de uitsluitlijst.
    document.addEventListener('click', function(event) {
      var trigger = event.target.closest('[data-track-click]');
      if (!trigger) return;

      // Alleen <a> en <button> taggen via deze conventie.
      var tag = trigger.tagName;
      if (tag !== 'A' && tag !== 'BUTTON') return;

      // Elementen die al een specifiek ecommerce- of funnel-event hebben
      // worden bewust uitgesloten om dubbeltelling te voorkomen.
      if (trigger.matches(TRACK_CLICK_EXCLUDE_SELECTORS)) return;
      if (trigger.closest('[data-product-card]')) return;

      trackSelectContent({
        category: trigger.dataset.trackCategory || '',
        action: trigger.dataset.trackAction || '',
        label: trigger.dataset.trackLabel || '',
        location: trigger.dataset.trackLocation || '',
        url: trigger.getAttribute('href') || '',
        sectionId: trigger.dataset.trackSectionId || '',
        blockId: trigger.dataset.trackBlockId || ''
      });
    });
  });

  /**
   * Custom event listeners voor AJAX cart updates
   */
  document.addEventListener('cart:add', function(e) {
    if (e.detail && e.detail.product) {
      trackAddToCart(e.detail.product, e.detail.quantity || 1);
    }
  });

  document.addEventListener('cart:remove', function(e) {
    if (e.detail && e.detail.product) {
      trackRemoveFromCart(e.detail.product, e.detail.quantity || 1);
    }
  });

  document.addEventListener('cart:open', function() {
    fetch('/cart.js')
      .then(function(response) { return response.json(); })
      .then(function(cart) {
        trackViewCart(cart);
      });
  });

})();
